import { AssessmentViews } from '@app/src/pages/Assessments/Assessment/assessmentViews'
import navigationLinks from '@app/src/wf-constants/navigationLinks'
import paths from '@app/src/wf-constants/paths'
import { generatePath } from 'react-router-dom'

/**
 * The LaunchDarkly flags that decide whether a page renders its MRT table. Product mapping tables
 * ship with the product mapping flow; the supplier table has its own rollout.
 *
 * Turning a page's table on silently breaks every inbound deep link still written in the pre-MRT
 * `?filters=` format: MRT reads its filters from localStorage, so those links land on whatever the user
 * last looked at. Part of migrating a page is sweeping its inbound links onto a link builder beside its
 * column registry (see providerTableLink.ts) - grep `stringifyQueryFilters` for the ones pointing at it.
 *
 * While a flag is rolling out both tables are live, so those builders state each link's filters twice,
 * once per format. Retiring a flag means deleting its legacy scene, its entry here, and the legacy half
 * of every link that points at it - the links keep emitting both until someone does.
 */
type MrtPageFlag = 'newProductMappingUi' | 'newTablesWithMaterialReactTable' | 'newSupplierRiskUi'

export type MrtPageFlags = Partial<Record<MrtPageFlag, boolean>>

/** A page with saved views: the route a page key's views link to (undefined for another page's key), and
 * the flag that turns its table on. */
type SavedViewPage = { routeFor: (pageKey: string) => string | undefined; flag: MrtPageFlag }

const fixedPage = (key: string, route: string, flag: MrtPageFlag): SavedViewPage => ({
  routeFor: pageKey => (pageKey === key ? route : undefined),
  flag,
})

// The assessment tabs are a page per template, each with columns of its own, so the template is in the key.
const ASSESSMENT_PAGE_KEY = new RegExp(
  `^assessment-(${[AssessmentViews.Overview, AssessmentViews.Sections, AssessmentViews.Answers].join('|')})-(\\d+)-mrt$`,
)

export const assessmentPageKey = (view: AssessmentViews, assessmentTemplateId: number | undefined): string =>
  `assessment-${view}-${assessmentTemplateId}-mrt`

/** Where a template's tabs keep the filters they share, so switching tabs keeps them. Not a page key. */
export const assessmentFilterKey = (assessmentTemplateId: number | undefined): string =>
  `assessment-${assessmentTemplateId}-mrt`

const assessmentPages: SavedViewPage = {
  routeFor: pageKey => {
    const [, view, assessmentTemplateId] = ASSESSMENT_PAGE_KEY.exec(pageKey) ?? []
    return view ? generatePath(paths.assessments, { view, assessmentTemplateId }) : undefined
  },
  flag: 'newTablesWithMaterialReactTable',
}

/**
 * The MRT pages that have saved views, each with the route its saved views link to and the flag that
 * turns its table on. This is the single source of truth: a page has saved views if (and only if) its
 * storageKey is matched here. Saved views are opt-in - tables not listed (admin/benchmark pages, tables
 * rendered inside dialogs) get no button and no nav link. Add an entry (its storageKey + route + flag)
 * to give a table saved views.
 */
const SAVED_VIEW_PAGES: ReadonlyArray<SavedViewPage> = [
  fixedPage('providers-mrt', navigationLinks.providers, 'newTablesWithMaterialReactTable'),
  fixedPage('products-mrt', navigationLinks.products, 'newProductMappingUi'),
  fixedPage('product-due-diligence-mrt', paths.productDueDiligence, 'newProductMappingUi'),
  fixedPage('supply-chain-relations-mrt', paths.mappingNodeCollection, 'newProductMappingUi'),
  // The Details tab rather than the bare page: the bare page redirects, and a redirect drops the
  // ?view= a saved view is carried in.
  fixedPage('topical-risk-mrt', navigationLinks.topicalRiskDetails, 'newSupplierRiskUi'),
  assessmentPages,
]

const findPage = (pageKey: string): { route: string; flag: MrtPageFlag } | undefined => {
  for (const page of SAVED_VIEW_PAGES) {
    const route = page.routeFor(pageKey)
    if (route) return { route, flag: page.flag }
  }
  return undefined
}

/** Whether a page has saved views - gates the toolbar button. */
export const isSavedViewPage = (pageKey: string): boolean => Boolean(findPage(pageKey))

/** The route a page's saved views link to, or undefined if the page has no saved views. pageKey
 * comes from the backend as a plain string. */
export const routeForPageKey = (pageKey: string): string | undefined => findPage(pageKey)?.route

/** Whether a page's table is currently on MRT. A saved view for a page whose flag is off would link to
 * the legacy table, which cannot apply it - so those views are hidden rather than shown as dead ends. */
export const isPageKeyEnabled = (pageKey: string, flags: MrtPageFlags): boolean => {
  const page = findPage(pageKey)
  return page ? flags[page.flag] === true : false
}

/** Whether any MRT page is enabled at all - gates fetching saved views in the first place. */
export const hasAnyMrtPageEnabled = (flags: MrtPageFlags): boolean =>
  SAVED_VIEW_PAGES.some(page => flags[page.flag] === true)
